<template>
  <div class="renovations-container">
    <clear-all-dialog v-if="showClearResetDialog"
                      :showDialog="showClearResetDialog"
                      :maxWidth="480"
                      :setWidth="480"
                      @confirm="confirmClearResetDialog"
                      @cancel="closeClearResetDialog">
    </clear-all-dialog>
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                attach=".renovations-container"
                @cancel="closeHelp">
      <market-rent-renovations-help :openSection="openHelpSection"></market-rent-renovations-help>
    </res-dialog>

    <v-row no-gutters justify="space-between" class="grid-controls">
      <v-col md="auto">
        <v-row class="function-controls" align="center" no-gutters>
          <v-col md="auto"><span class="natural-language-words">I want to renovate</span></v-col>
          <v-col md="auto"><res-number-input-field :readonly="readonly" v-model="worksheetParameters.desiredRenovatedUnitsPercentage" :minValue="minPercent" :maxValue="maxPercent" class="natural-language-inputs" suffix="%" @input="onInputDesiredRenovatedUnitsPercentage"></res-number-input-field></v-col>
          <v-col md="auto"><span class="natural-language-words">=</span></v-col>
          <v-col md="auto"><res-number-input-field :readonly="readonly" v-model="worksheetParameters.desiredRenovatedUnitsCount" :minValue="minNumberOfUnits" :maxValue="totalOfUnits" class="natural-language-inputs" @input="onInputDesiredRenovatedUnitsCount"></res-number-input-field></v-col>
          <v-col md="auto"><span class="natural-language-words">of</span></v-col>
          <v-col md="auto"><res-number-input-field :readonly="readonly" v-model="totalOfUnits" :disabled="true" class="natural-language-inputs"></res-number-input-field></v-col>
          <v-col md="auto"><span class="natural-language-words">units at</span></v-col>
          <v-col md="auto"><res-number-input-field :readonly="readonly" v-model="worksheetParameters.defaultRenovationPremiumPerUnit" :minValue="minPremiumAmount" prefix="$" class="natural-language-inputs" @input="onInputDefaultRenovationPremiumPerUnit"></res-number-input-field></v-col>
          <v-col md="auto"><span class="natural-language-words">per unit at the</span></v-col>
          <v-col md="auto">
            <res-radio-group :value="worksheetParameters.trackRenovationsAtUnitTypeLevel"
                             :readonly="readonly"
                             :options="trackRenovationsAtUnitTypeLevelOptions"
                             @input="trackRenovationsAtUnitTypeLevelChanged" :key="viewChangeIteration">
            </res-radio-group>
          </v-col>
        </v-row>
      </v-col>
      <v-col md="auto">
        <div class="help-controls">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="openHelp(null)" class="pb-2" color="var(--v-resBlue-darken1)">mdi-help-circle</v-icon>
            </template>
            <span>Help</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" class="ml-3 mb-2" :color="commentIconColor" @click="openComments">{{ commentIcon  }}</v-icon>
            </template>
            <span>Comments/Flags</span>
          </v-tooltip>
          <v-menu left offset-y close-on-click @input="updateMenuState">
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon :color="menuColor" class="ellipses-control">
                <v-icon dense>{{menuIcon}}</v-icon>
              </v-btn>
            </template>
            <v-list class="ellipses-control-option-list">
              <v-list-item @click="openComments">
                <v-list-item-title class="ellipses-control-option">Add Flag or Comment</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-list-item v-bind="attrs" v-on="on" @click="clearAndResetPrompt">
                    <v-list-item-title class="ellipses-control-option">Clear & Reset</v-list-item-title>
                  </v-list-item>
                </template>
                <span>This will clear this tab of any entered information and revert to the initial values</span>
              </v-tooltip>
            </v-list>
          </v-menu>
        </div>
      </v-col>
    </v-row>
    <div class="grid-container" :style="gridContainerStyle">
      <ag-grid-vue style="width: 100%; height: 100%;"
                   class="ag-theme-material"
                   :gridOptions="gridOptions"
                   :columnDefs="currentHeaders"
                   :rowData="currentData"
                   :isExternalFilterPresent="shouldRowsBeFiltered"
                   :doesExternalFilterPass="shouldShowRow"
                   @cell-double-clicked="onCellDoubleClicked"
                   @cell-value-changed="onCellValueChanged">
      </ag-grid-vue>
    </div>
  </div>
</template>

<script>
  import YearGrid from '@/mixins/YearGrid';
  import HelpableMixin from '@/mixins/Helpable.js';
  import ClearAllDialog from "@/components/budgets/dialogs/ClearAllDialog";
  import ResDialog from '@/components/base/ResDialog.vue';
  import ResRadioGroup from '@/components/base/ResRadioGroup';
  import ResNumberInputField from '@/components/base/ResNumberInputField';

  import { AgGridVue } from "ag-grid-vue";
  import GroupedRowButton from '@/components/base/ag-grid/GroupedRowButton';
  import LeaseExpiresReadonlyCell from '@/components/base/ag-grid/LeaseExpiresReadonlyCell';
  import NumOutOfNumReadonlyCell from '@/components/base/ag-grid/NumOutOfNumReadonlyCell';

  import MarketRentRenovationsHelp from '@/components/help/MarketRentRenovationsHelp.vue';

  import budgetService from '@/services/budget';
  import marketRentService from '@/services/market-rent';
  import worksheetService from '@/services/worksheet';
  import budgetGlAccountservice from '@/services/budget-gl-account';
  import { mapGetters, mapActions } from 'vuex';

  const NUM_OF_UNIT_RENOS = 'number-of-unit-renos';
  const RENO_REVENUE = 'reno-revenue';
  const PROJECTED_RENO_REVENUE = 'projected-reno-revenue';
  const UNIT_TYPE_HEADER = 'unit-type-header';
  const UNIT_TYPE_NUM_OF_UNIT_RENOS = 'unit-type-num-of-unit-renos';
  const UNIT_TYPE_RENO_REVENUE = 'unit-type-reno-revenue';
  const UNIT_TYPE_PROJECTED_RENO_REVENUE = 'unit-type-projected-reno-revenue';
  const UNIT_HEADER = 'unit-header';
  const UNIT = 'unit';
  const TOTAL_COL_ID = 'total';

  export default {
    name: 'Renovations',
    mixins: [
      YearGrid,
      HelpableMixin
    ],
    components: {
      AgGridVue,
      ClearAllDialog,
      ResRadioGroup,
      ResNumberInputField,
      MarketRentRenovationsHelp,
      ResDialog,
      /* eslint-disable vue/no-unused-components */
      GroupedRowButton, // used by AG Grid
      LeaseExpiresReadonlyCell,
      NumOutOfNumReadonlyCell,
      /* eslint-enable vue/no-unused-components */
    },
    props: {
      budgetIterationId: {
        required: true
      },
      commentIteration: {},
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data: function () {
      return {
        NUM_OF_UNIT_RENOS: NUM_OF_UNIT_RENOS,
        RENO_REVENUE: RENO_REVENUE,
        PROJECTED_RENO_REVENUE: PROJECTED_RENO_REVENUE,
        UNIT_TYPE_HEADER: UNIT_TYPE_HEADER,
        UNIT_TYPE_NUM_OF_UNIT_RENOS: UNIT_TYPE_NUM_OF_UNIT_RENOS,
        UNIT_TYPE_RENO_REVENUE: UNIT_TYPE_RENO_REVENUE,
        UNIT_TYPE_PROJECTED_RENO_REVENUE: UNIT_TYPE_PROJECTED_RENO_REVENUE,
        UNIT_HEADER: UNIT_HEADER,
        UNIT: UNIT,
        PERIOD_INDEX_00_AMOUNT: this.getColId(0),
        PERIOD_INDEX_01_AMOUNT: this.getColId(1),
        PERIOD_INDEX_02_AMOUNT: this.getColId(2),
        PERIOD_INDEX_03_AMOUNT: this.getColId(3),
        PERIOD_INDEX_04_AMOUNT: this.getColId(4),
        PERIOD_INDEX_05_AMOUNT: this.getColId(5),
        PERIOD_INDEX_06_AMOUNT: this.getColId(6),
        PERIOD_INDEX_07_AMOUNT: this.getColId(7),
        PERIOD_INDEX_08_AMOUNT: this.getColId(8),
        PERIOD_INDEX_09_AMOUNT: this.getColId(9),
        PERIOD_INDEX_10_AMOUNT: this.getColId(10),
        PERIOD_INDEX_11_AMOUNT: this.getColId(11),
        TOTAL_COL_ID: TOTAL_COL_ID,
        worksheetParameters: {
          trackRenovationsAtUnitTypeLevel: false, // default to property level view
          desiredRenovatedUnitsPercentage: null,
          desiredRenovatedUnitsCount: null,
          defaultRenovationPremiumPerUnit: null,
          trackOccupancyAtUnitTypeLevel: null,
          trackActualRentAtUnitTypeLevel: null
        },
        minPercent: 1,
        maxPercent: 100,
        minNumberOfUnits: 1,
        minPremiumAmount: 0,
        totalOfUnits: 0,
        trackRenovationsAtUnitTypeLevelOptions: [
          { label: 'Property Level', value: false },
          { label: 'Unit Type Level', value: true },
        ],
        fiscalYear: null,
        fiscalYearStartMonth: null,
        fiscalYearHeaders: [],
        budgetIterationLastModifiedTimestamp: null,
        summaryRefreshIsQueued: false,
        gridContainerHeight: 128,
        numberOfUnitRenosRowId: 'number-of-unit-renos-0',
        propRenoRevenueRowId: 'reno-revenue-0',
        propProjectedRenoRevenuesRowId: 'projected-reno-revenue-0',
        availableColumns: [],
        gridOptions: {
          headerHeight: 36,
          rowHeight: 36,
          domLayout: 'autoHeight',
          columnDefs: this.availableColumns,
          getRowNodeId: this.getRowNodeId,
          alignedGrids: [],
          suppressClickEdit: true,
          stopEditingWhenGridLosesFocus: true,
          frameworkComponents: {
            'GroupedRowButton': 'GroupedRowButton',
            'LeaseExpiresReadonlyCell': 'LeaseExpiresReadonlyCell',
            'NumOutOfNumReadonlyCell': 'NumOutOfNumReadonlyCell'
          },
        },
        renovationData: [],
        showClearResetDialog: false,
        newViewDesired: null,
        viewChangeIteration: 0,
        tabHasUnresolvedComments: false,
        tabHasComments: false,
        menuOpen: false
      };
    },
    methods: {
      ...mapActions(['changeLoading', 'activateErrorDialog']),
      updateMenuState(isOpen) {
        this.menuOpen = isOpen;
      },
      async communicateEntriesUpdated() {
        var t = this;

        // refresh dependencies
        if (!t.summaryRefreshIsQueued) {
          t.summaryRefreshIsQueued = true;
          setTimeout(async () => {
            t.summaryRefreshIsQueued = false;
            t.$eventHub.$emit('renovationPremiumEntriesUpdated', t.budgetIterationId);
            // only updating GL mapping for these concessions, for now. Setting a timeout for the other concession entries is going to be different.
            var worksheetEntryTypeIds = [3, 4, 24];
            /*  Enums.WorksheetEntryTypes.NumberOfUnitsToRenovate,
                    Enums.WorksheetEntryTypes.RenovationIncreaseAmount,
                    Enums.WorksheetEntryTypes.RenovationPremiumAmount
                        */
            await budgetGlAccountservice.updateBasedOnWorksheets(t.budgetIterationId,
              worksheetEntryTypeIds, // List<byte>
              [], // List<Guid>
              t.budgetIterationLastModifiedTimestamp);
          }, 2500); // reduce refresh calls when multiple edits happen at once
        }
      },
      async onInputDesiredRenovatedUnitsPercentage(newValue) {
        this.worksheetParameters.desiredRenovatedUnitsCount = this.halfRoundUp(this.totalOfUnits * newValue / 100, 0);
        await this.saveWorksheetParameters();        
      },
      async onInputDesiredRenovatedUnitsCount(newValue) {
        this.worksheetParameters.desiredRenovatedUnitsPercentage = this.halfRoundUp(newValue / this.totalOfUnits * 100, 0);
        await this.saveWorksheetParameters();
      },
      async onInputDefaultRenovationPremiumPerUnit() {
        await this.saveWorksheetParameters();
      },
      async saveWorksheetParameters() {
        this.changeLoading(true);
        var data = await worksheetService.updateWorksheetParameters(this.budgetIterationId, this.worksheetParameters, this.budgetIterationLastModifiedTimestamp);

        // update timestamp
        if (data.lastModifiedTimestamp) {
          this.budgetIterationLastModifiedTimestamp = data.lastModifiedTimestamp;          
        }
        this.changeLoading(false);
      },
      clearAndResetPrompt() {
        if (this.readonly) {
          this.$emit('denied-status');
          return;
        }
        this.showClearResetDialog = true;
      },
      closeClearResetDialog() {
        this.worksheetParameters.trackRenovationsAtUnitTypeLevel = this.newViewDesired == null ? this.worksheetParameters.trackRenovationsAtUnitTypeLevel : !this.newViewDesired;
        this.newViewDesired = null;
        this.viewChangeIteration++;
        this.showClearResetDialog = false;
      },
      async confirmClearResetDialog() {
        console.debug("clear and reset");
        this.worksheetParameters.trackRenovationsAtUnitTypeLevel = this.newViewDesired == null ? this.worksheetParameters.trackRenovationsAtUnitTypeLevel : this.newViewDesired;
        this.worksheetParameters.desiredRenovatedUnitsPercentage = 0.00;
        this.worksheetParameters.desiredRenovatedUnitsCount = 0;
        this.worksheetParameters.defaultRenovationPremiumPerUnit = 0.00;
        await this.saveWorksheetParameters();

        var response = await marketRentService.resetRenovationsEntries(this.budgetIterationId, this.budgetIterationLastModifiedTimestamp);
        if (response) {
          await this.loadRenovationData();
          await this.communicateEntriesUpdated();
          this.$eventHub.$emit('showAlertNotification', 'Renovations entries cleared and reset successfully!');
        }
        else {
          this.activateErrorDialog({ title: 'Sync Failed', message: 'An error occurred clearing and resetting the data. Please try again.' });
        }
        this.showClearResetDialog = false;

        this.refreshDataFilter();
      },
      trackRenovationsAtUnitTypeLevelChanged(viewDesired) {
        this.newViewDesired = viewDesired;
        this.showClearResetDialog = true;
      },
      async load() {
        await this.loadGridConfiguration();
        await this.loadRenovationData(false);
      },
      async loadGridConfiguration() {
        let t = this;
        t.changeLoading(true);

        let settings = await budgetService.getBudgetSettings(t.budgetIterationId);
        t.fiscalYear = settings.year;
        t.fiscalYearStartMonth = settings.startMonth;
        t.budgetIterationLastModifiedTimestamp = settings.lastModifiedTimestamp;

        // setup column headers
        t.fiscalYearHeaders = t.getFiscalYearMonths(t.fiscalYearStartMonth);

        t.availableColumns = [];

        t.availableColumns.push({
          headerName: "", colId: "showUnitRecords", width: 16, pinned: 'left', suppressSizeToFit: true, cellStyle: { margin: 0, padding: 0 },
          field: "showUnitRecords",
          valueFormatter: function (params) {
            return t.rowIsHeader(params.data.rowType) && params.data.hasUnitRecords ? params.data.showUnitRecords : '';
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellRendererSelector: t.selectGroupedCellRenderer,
        });
        t.availableColumns.push({
          headerName: "", colId: "label", width: 96, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "label",
          colSpan: function (params) {
            switch (params.data.rowType) {
              case t.NUM_OF_UNIT_RENOS:
              case t.RENO_REVENUE:
              case t.PROJECTED_RENO_REVENUE:
                return 3;
              case t.UNIT_TYPE_RENO_REVENUE:
              case t.UNIT_TYPE_PROJECTED_RENO_REVENUE:
                return 2;
              default:
                return 1;
            }
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function (params) {
            var style = { 'margin-right': 0, 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold' };
            if (t.rowIsHeader(params.data.rowType)) {
              style['line-height'] = 'normal';
            }
            switch (params.data.rowType) {
              case t.NUM_OF_UNIT_RENOS:
              case t.RENO_REVENUE:
              case t.PROJECTED_RENO_REVENUE:
                style['text-align'] = 'right';
                break;
              case t.UNIT_TYPE_RENO_REVENUE:
              case t.UNIT_TYPE_PROJECTED_RENO_REVENUE:
                style['text-align'] = 'right';
                break;
              default:
                style['text-align'] = 'center';
                break;
            }
            return style;
          },
          cellRenderer: function (params) {
            return t.rowIsHeader(params.data.rowType) ?
              `<span style="font-weight: bold">Unit</span>
               <br/>
               <span style="font-weight: bold">Type</span>` :
              `<span>${params.data.label}</span>`;
          }
        });
        t.availableColumns.push({
          headerName: "", colId: "subLabel", width: 60, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "subLabel",
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function (params) {
            var style = { 'margin-left': 0, 'padding-right': 0, 'padding-left': 0, 'font-weight': 'bold', 'text-align': 'center' };
            if (t.rowIsHeader(params.data.rowType)) {
              style['line-height'] = 'normal';
            }
            return style;
          },
          cellRenderer: function (params) {
            return params.data.rowType == t.UNIT_TYPE_HEADER ?
              `<span style="font-weight: bold">No. of</span>
               <br/>
               <span style="font-weight: bold">Units</span>` :
              params.data.rowType == t.UNIT_HEADER ?
                `<span style="font-weight: bold">Unit</span>
               <br/>
               <span style="font-weight: bold">Number</span>` :
                `<span>${params.data.subLabel}</span>`;
          }
        });
        t.availableColumns.push({
          headerName: "", colId: "spacer", width: 60, pinned: 'left', suppressSizeToFit: true, suppressNavigable: true, field: "spacer",
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: {margin: 0, padding: 0},
          cellRenderer: function () {
            return `<span> </span>`;
          }
        });
        // months
        for (var i = 0; i < t.fiscalYearHeaders.length; i++) {
          t.availableColumns.push({
            headerName: t.fiscalYearHeaders[i], colId: t.getColId(i), width: 96, editable: t.isCellEditable, field: t.getColId(i),
            valueParser: t.entryParser,
            valueGetter: t.entryGetter,
            valueFormatter: function (params) {
              return params.data.rowType == t.UNIT ? '-' : t.rowIsHeader(params.data.rowType) ? params.colDef.headerName : t.entryFormatter(params);
            },
            cellClass: function (params) {
              return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : '';
            },
            cellClassRules: {
              'readonly-column': function (params) {
                return !t.rowIsHeader(params.data.rowType) && !t.isCellEditable(params);
              }
            },
            cellStyle: function (params) {
              var style = { 'height': '100%' };
              if (t.rowIsHeader(params.data.rowType)) { style = style + ' font-weight: bold'; }
              return style;
            },
            cellRendererSelector: t.selectPeriodCellRenderer,
          });
        }

        t.availableColumns.push({
          headerName: "TOTAL/AVG", colId: t.TOTAL_COL_ID, width: 125, pinned: 'right', suppressSizeToFit: true, field: t.TOTAL_COL_ID,
          valueGetter: t.entryGetter,
          valueFormatter: function (params) {
            return params.data.rowType == t.UNIT ? `-` : t.rowIsHeader(params.data.rowType) ? params.colDef.headerName : t.entryFormatter(params);
          },
          cellClass: function (params) {
            return t.rowIsHeader(params.data.rowType) ? 'sub-header-column' : 'readonly-column';
          },
          cellStyle: function (params) {
            var style = { 'height': '100%' };
            if (t.rowIsHeader(params.data.rowType)) { style = style + ' font-weight: bold';}
            return style;
          },
          cellRendererSelector: t.selectTotalCellRenderer,
        });

        t.changeLoading(false);
      },
      async loadRenovationData() {
        let t = this;
        t.changeLoading(true);
        var  data = await marketRentService.getRenovationsEntries(t.budgetIterationId);
        

        t.worksheetParameters.trackRenovationsAtUnitTypeLevel = data.trackRenovationsAtUnitTypeLevel;
        t.worksheetParameters.desiredRenovatedUnitsPercentage = data.desiredRenovatedUnitsPercentage;
        t.worksheetParameters.desiredRenovatedUnitsCount = data.desiredRenovatedUnitsCount;
        t.worksheetParameters.defaultRenovationPremiumPerUnit = data.defaultRenovationPremiumPerUnit;
        t.totalOfUnits = data.totalUnitCount;

        // update timestamp
        if (data.lastModifiedTimestamp) {
          this.budgetIterationLastModifiedTimestamp = data.lastModifiedTimestamp;
        }

        t.renovationData = [];
        var utCount = 0;
        var maxUnitCount = 0;

        // only load property level rows
        if (!t.worksheetParameters.trackRenovationsAtUnitTypeLevel) {
          t.renovationData.push(t.initPropertyRow(data.propertyLevelEntries.numberOfUnitRenovations, t.NUM_OF_UNIT_RENOS + '-' + t.numberOfUnitRenosRowId,
            t.NUM_OF_UNIT_RENOS, t.numberOfUnitRenosRowId, true));

          t.renovationData.push(t.initPropertyRow(data.propertyLevelEntries.renovationRevenues, t.RENO_REVENUE + '-' + t.propRenoRevenueRowId,
            t.RENO_REVENUE, t.propRenoRevenueRowId, true));

          t.renovationData.push(t.initPropertyRow(data.propertyLevelEntries.projectedRenovationRevenues, t.PROJECTED_RENO_REVENUE + '-' + t.propProjectedRenoRevenuesRowId,
            t.PROJECTED_RENO_REVENUE, t.propProjectedRenoRevenuesRowId, true));

        } else { // load unit level rows
          // if we're working at the unit type level, this row is only autocalculated and not actual data numbers
          var numberofunitrenos = {
            rowType: t.NUM_OF_UNIT_RENOS,
            rowId: t.numberOfUnitRenosRowId,
            worksheetEntryTypeId: null,
            label: '# of Unit Renovations',
            subLabel: 0,
            periodIndex00: { amount: null },
            periodIndex01: { amount: null },
            periodIndex02: { amount: null },
            periodIndex03: { amount: null },
            periodIndex04: { amount: null },
            periodIndex05: { amount: null },
            periodIndex06: { amount: null },
            periodIndex07: { amount: null },
            periodIndex08: { amount: null },
            periodIndex09: { amount: null },
            periodIndex10: { amount: null },
            periodIndex11: { amount: null },
            total: 0,
            hasUnitRecords: false
          };
          t.renovationData.push(t.initPropertyRow(numberofunitrenos, t.NUM_OF_UNIT_RENOS + '-' + t.numberOfUnitRenosRowId,
            t.NUM_OF_UNIT_RENOS, t.numberOfUnitRenosRowId, false));

          // use rowType to do conditional rendering of each row - header type gets it's own hand built cell components to make it look like a header and not be involved with the math side of things
          var unittypeheaderrow1 = {
            rowType: this.UNIT_TYPE_HEADER,
            rowId: 'unit-type-header-0',
            label: '',
            hasUnitRecords: false
          };
          t.renovationData.push(t.initRowHeader(unittypeheaderrow1, unittypeheaderrow1.rowId, false));

          data.unitTypeLevelEntries.forEach((ut) => {
            t.renovationData.push(t.initUnitTypeRow(ut.numberOfUnitRenovations, ut.subLabel, t.UNIT_TYPE_NUM_OF_UNIT_RENOS + '-' + ut.unitTypeId,
              t.UNIT_TYPE_NUM_OF_UNIT_RENOS, ut.unitTypeId, ut.unitLevelEntries.length > 0, true));

            t.renovationData.push(t.initUnitTypeRow(ut.renovationRevenues, ut.subLabel, t.UNIT_TYPE_RENO_REVENUE + '-' + ut.unitTypeId,
              t.UNIT_TYPE_RENO_REVENUE, ut.unitTypeId, false, true));

            t.renovationData.push(t.initUnitTypeRow(ut.projectedRenovationRevenues, ut.subLabel, t.UNIT_TYPE_PROJECTED_RENO_REVENUE + '-' + ut.unitTypeId,
              t.UNIT_TYPE_PROJECTED_RENO_REVENUE, ut.unitTypeId, false, true));

            // use rowType to do conditional rendering of each row - header type gets it's own hand built cell components to make it look like a header and not be involved with the math side of things
            var unitheaderrow = {
              rowType: this.UNIT_HEADER,
              rowId: 'unit-header-' + ut.unitTypeId,
              label: '',
              hasUnitRecords: false
            };
            t.renovationData.push(t.initRowHeader(unitheaderrow, t.UNIT_TYPE_NUM_OF_UNIT_RENOS + '-' + ut.unitTypeId, true));

            ut.unitLevelEntries.forEach((unitChild) => {
              t.renovationData.push(t.initUnitRow(unitChild, t.UNIT_TYPE_NUM_OF_UNIT_RENOS + '-' + ut.unitTypeId, unitChild.unitId));
            });
            utCount++;
            if (maxUnitCount < ut.unitLevelEntries.length) { maxUnitCount = ut.unitLevelEntries.length;  }
          });          
        }
        

        t.gridContainerHeight = !t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? 147 : (108 * utCount) + (36 * maxUnitCount) + (maxUnitCount > 0 ? 113 : 75);
        t.gridOptions.api.setHeaderHeight(!t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? 36 : 0);
        t.sizeGridColumns(t.gridOptions);

        t.changeLoading(false);
      },
      initRowHeader(data, tag, isUnitHeader) {
        return {
          tag: tag,
          rowType: data.rowType,
          rowId: data.rowId,
          label: data.label,
          isEditable: false,

          // unit groupings
          hasUnitRecords: false,
          showUnitRecords: false,
          isUnitRecord: isUnitHeader
        }
      },
      initPropertyRow(data, tag, rowType, rowId, isEditable) {
        return {
          tag: tag,
          rowType: rowType,
          rowId: rowId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          label: data.label,
          subLabel: data.subLabel,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,
          total: data.total,
          isEditable: isEditable,

          // unit groupings
          hasUnitRecords: false,
          showUnitRecords: false,
          isUnitRecord: false
        }
      },
      initUnitTypeRow(data, subLabel, tag, rowType, rowId, hasUnitRecords, isEditable) {
        return {
          tag: tag,
          rowType: rowType,
          rowId: rowId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          label: data.label,
          subLabel: subLabel,
          periodIndex00Amount: data.periodIndex00.amount,
          periodIndex01Amount: data.periodIndex01.amount,
          periodIndex02Amount: data.periodIndex02.amount,
          periodIndex03Amount: data.periodIndex03.amount,
          periodIndex04Amount: data.periodIndex04.amount,
          periodIndex05Amount: data.periodIndex05.amount,
          periodIndex06Amount: data.periodIndex06.amount,
          periodIndex07Amount: data.periodIndex07.amount,
          periodIndex08Amount: data.periodIndex08.amount,
          periodIndex09Amount: data.periodIndex09.amount,
          periodIndex10Amount: data.periodIndex10.amount,
          periodIndex11Amount: data.periodIndex11.amount,
          total: data.total,
          isEditable: isEditable,

          // unit groupings
          hasUnitRecords: hasUnitRecords,
          showUnitRecords: false,
          isUnitRecord: false
        }
      },
      initUnitRow(data, tag, rowId) {
        return {
          tag: tag,
          rowType: this.UNIT,
          rowId: rowId,
          worksheetEntryTypeId: null,
          label: data.label,
          subLabel: data.subLabel,
          periodIndex00Amount: data.periodIndex00,
          periodIndex01Amount: data.periodIndex01,
          periodIndex02Amount: data.periodIndex02,
          periodIndex03Amount: data.periodIndex03,
          periodIndex04Amount: data.periodIndex04,
          periodIndex05Amount: data.periodIndex05,
          periodIndex06Amount: data.periodIndex06,
          periodIndex07Amount: data.periodIndex07,
          periodIndex08Amount: data.periodIndex08,
          periodIndex09Amount: data.periodIndex09,
          periodIndex10Amount: data.periodIndex10,
          periodIndex11Amount: data.periodIndex11,
          total: '-',
          isEditable: false,

          // unit groupings
          hasUnitRecords: false,
          showUnitRecords: false,
          isUnitRecord: true
        }
      },

      getRowNodeId(data) {
        return data.rowType + "-" + data.rowId;
      },
      getColId(periodIndex) {
        return periodIndex < 10 ? "periodIndex0" + periodIndex + "Amount" : "periodIndex" + periodIndex + "Amount";
      },
      getPeriodIndex(colId) {
        const sub1 = 'periodIndex';
        const sub2 = 'Amount';
        if (colId.indexOf(sub1) < 0 || colId.indexOf('Amount') < 0) return false;
        var SP = colId.indexOf(sub1) + sub1.length;
        var string1 = colId.substr(0, SP);
        var string2 = colId.substr(SP);
        var TP = string1.length + string2.indexOf(sub2);
        var indexString = colId.substring(SP, TP);
        return Number(indexString);
      },    
      generatePropWorksheetEntriesFromData(data) {
        return {
                worksheetEntryTypeId: data.worksheetEntryTypeId,
                periodIndex00: data.periodIndex00Amount,
                periodIndex01: data.periodIndex01Amount,
                periodIndex02: data.periodIndex02Amount,
                periodIndex03: data.periodIndex03Amount,
                periodIndex04: data.periodIndex04Amount,
                periodIndex05: data.periodIndex05Amount,
                periodIndex06: data.periodIndex06Amount,
                periodIndex07: data.periodIndex07Amount,
                periodIndex08: data.periodIndex08Amount,
                periodIndex09: data.periodIndex09Amount,
                periodIndex10: data.periodIndex10Amount,
                periodIndex11: data.periodIndex11Amount,
        };
      },
      generateUnitTypeWorksheetEntriesFromData(data) {
        return {
          unitTypeId: data.rowId,
          worksheetEntryTypeId: data.worksheetEntryTypeId,
          periodIndex00: data.periodIndex00Amount,
          periodIndex01: data.periodIndex01Amount,
          periodIndex02: data.periodIndex02Amount,
          periodIndex03: data.periodIndex03Amount,
          periodIndex04: data.periodIndex04Amount,
          periodIndex05: data.periodIndex05Amount,
          periodIndex06: data.periodIndex06Amount,
          periodIndex07: data.periodIndex07Amount,
          periodIndex08: data.periodIndex08Amount,
          periodIndex09: data.periodIndex09Amount,
          periodIndex10: data.periodIndex10Amount,
          periodIndex11: data.periodIndex11Amount,
        };
      },
      isPeriodIndexAmountColumn(field) {
        switch (field) {
          case this.PERIOD_INDEX_00_AMOUNT:
          case this.PERIOD_INDEX_01_AMOUNT:
          case this.PERIOD_INDEX_02_AMOUNT:
          case this.PERIOD_INDEX_03_AMOUNT:
          case this.PERIOD_INDEX_04_AMOUNT:
          case this.PERIOD_INDEX_05_AMOUNT:
          case this.PERIOD_INDEX_06_AMOUNT:
          case this.PERIOD_INDEX_07_AMOUNT:
          case this.PERIOD_INDEX_08_AMOUNT:
          case this.PERIOD_INDEX_09_AMOUNT:
          case this.PERIOD_INDEX_10_AMOUNT:
          case this.PERIOD_INDEX_11_AMOUNT:
            return true;
          default:
            return false;
        }
      },
      onCellDoubleClicked(params) {
        if (!this.isPeriodIndexAmountColumn(params.colDef.field)) {
          return;
        }
        params.api.startEditingCell({ rowIndex: params.rowIndex, colKey: params.colDef.field });
      },


      async onCellValueChanged(params) {
        var t = this;

        // double check these are data edit rows and columns that can be edited
        switch (params.colDef.colId) {
          case t.PERIOD_INDEX_00_AMOUNT:
          case t.PERIOD_INDEX_01_AMOUNT:
          case t.PERIOD_INDEX_02_AMOUNT:
          case t.PERIOD_INDEX_03_AMOUNT:
          case t.PERIOD_INDEX_04_AMOUNT:
          case t.PERIOD_INDEX_05_AMOUNT:
          case t.PERIOD_INDEX_06_AMOUNT:
          case t.PERIOD_INDEX_07_AMOUNT:
          case t.PERIOD_INDEX_08_AMOUNT:
          case t.PERIOD_INDEX_09_AMOUNT:
          case t.PERIOD_INDEX_10_AMOUNT:
          case t.PERIOD_INDEX_11_AMOUNT:
            t.changeLoading(true);

            console.debug("cell changed: " + params.data.rowType + ', ' + params.data.rowId + ', ' + params.colDef.colId + ', ' + params.newValue);

            var periodIndex = t.getPeriodIndex(params.colDef.colId);
            var calculateForPeriod = false;

            var itemsToUpdateGrid = [];

            var datasToSave = [];           

            var periodAmount;

            var renoRevenueNode;
            var renoRevenueData;
            var projRenoRevenueNode;
            var projRenoRevenueData;

            if (!t.worksheetParameters.trackRenovationsAtUnitTypeLevel) {
              if (params.data.rowType == t.NUM_OF_UNIT_RENOS) {
                renoRevenueNode = t.gridOptions.api.getRowNode(t.RENO_REVENUE + '-' + t.propRenoRevenueRowId);
                renoRevenueData = renoRevenueNode.data;
              }
              projRenoRevenueNode = t.gridOptions.api.getRowNode(t.PROJECTED_RENO_REVENUE + '-' + t.propProjectedRenoRevenuesRowId);
              projRenoRevenueData = projRenoRevenueNode.data;
            } else {
              if (params.data.rowType == t.UNIT_TYPE_NUM_OF_UNIT_RENOS) {
                renoRevenueNode = t.gridOptions.api.getRowNode(t.UNIT_TYPE_RENO_REVENUE + '-' + params.data.rowId);
                renoRevenueData = renoRevenueNode.data;
              }
              projRenoRevenueNode = t.gridOptions.api.getRowNode(t.UNIT_TYPE_PROJECTED_RENO_REVENUE + '-' + params.data.rowId);
              projRenoRevenueData = projRenoRevenueNode.data;
            }

            // similar row types use the same basic calculations
            switch (params.data.rowType) {
              case t.NUM_OF_UNIT_RENOS:
              case t.UNIT_TYPE_NUM_OF_UNIT_RENOS:
                datasToSave.push(t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? t.generateUnitTypeWorksheetEntriesFromData(params.data) : t.generatePropWorksheetEntriesFromData(params.data));

           
                     for (var i = 0; i < 12; i++) {
                      calculateForPeriod = periodIndex <= i;

                      var n = Number(t.getPeriodAmount(i, params.data, false));
                      if (isNaN(n)) { n = 0; }
                      periodAmount = isNaN(n) ? 0 : n;

                      switch (i) {
                        case 0:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex00Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex00Amount = t.parseCurrency(renoRevenueData.periodIndex00Amount);
                          }
                          break;
                        case 1:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex01Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex01Amount = t.parseCurrency(projRenoRevenueData.periodIndex00Amount) + t.parseCurrency(renoRevenueData.periodIndex01Amount);
                          }
                          break;
                        case 2:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex02Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex02Amount = t.parseCurrency(projRenoRevenueData.periodIndex01Amount) + t.parseCurrency(renoRevenueData.periodIndex02Amount);
                          }
                          break;
                        case 3:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex03Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex03Amount = t.parseCurrency(projRenoRevenueData.periodIndex02Amount) + t.parseCurrency(renoRevenueData.periodIndex03Amount);
                          }
                          break;
                        case 4:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex04Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex04Amount = t.parseCurrency(projRenoRevenueData.periodIndex03Amount) + t.parseCurrency(renoRevenueData.periodIndex04Amount);
                          }
                          break;
                        case 5:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex05Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex05Amount = t.parseCurrency(projRenoRevenueData.periodIndex04Amount) + t.parseCurrency(renoRevenueData.periodIndex05Amount);
                          }
                          break;
                        case 6:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex06Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex06Amount = t.parseCurrency(projRenoRevenueData.periodIndex05Amount) + t.parseCurrency(renoRevenueData.periodIndex06Amount);
                          }
                          break;
                        case 7:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex07Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex07Amount = t.parseCurrency(projRenoRevenueData.periodIndex06Amount) + t.parseCurrency(renoRevenueData.periodIndex07Amount);
                          }
                          break;
                        case 8:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex08Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex08Amount = t.parseCurrency(projRenoRevenueData.periodIndex07Amount) + t.parseCurrency(renoRevenueData.periodIndex08Amount);
                          }
                          break;
                        case 9:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex09Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex09Amount = t.parseCurrency(projRenoRevenueData.periodIndex08Amount) + t.parseCurrency(renoRevenueData.periodIndex09Amount);
                          }
                          break;
                        case 10:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex10Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex10Amount = t.parseCurrency(projRenoRevenueData.periodIndex09Amount) + t.parseCurrency(renoRevenueData.periodIndex10Amount);
                          }
                          break;
                        case 11:
                          if (calculateForPeriod) {
                            renoRevenueData.periodIndex11Amount = periodAmount != null ? periodAmount * t.parseCurrency(t.worksheetParameters.defaultRenovationPremiumPerUnit) : null;
                            projRenoRevenueData.periodIndex11Amount = t.parseCurrency(projRenoRevenueData.periodIndex10Amount) + t.parseCurrency(renoRevenueData.periodIndex11Amount);
                          }
                          break;
                        default:
                          break;
                      }
                }

                itemsToUpdateGrid.push(renoRevenueData);
                itemsToUpdateGrid.push(projRenoRevenueData);
                datasToSave.push(t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? t.generateUnitTypeWorksheetEntriesFromData(renoRevenueData) : t.generatePropWorksheetEntriesFromData(renoRevenueData));
                datasToSave.push(t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? t.generateUnitTypeWorksheetEntriesFromData(projRenoRevenueData) : t.generatePropWorksheetEntriesFromData(projRenoRevenueData));

                t.gridOptions.api.applyTransaction({ update: itemsToUpdateGrid });
                break;
              case t.RENO_REVENUE:
              case t.UNIT_TYPE_RENO_REVENUE: 
                datasToSave.push(t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? t.generateUnitTypeWorksheetEntriesFromData(params.data) : t.generatePropWorksheetEntriesFromData(params.data));
        
                    for (var k = 0; k < 12; k++) {
                      calculateForPeriod = periodIndex <= k;
                      periodAmount = t.parseCurrency(t.getPeriodAmount(k, params.data, false));

                      switch (k) {
                        case 0:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex00Amount = periodAmount;
                          }
                          break;
                        case 1:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex01Amount = t.parseCurrency(projRenoRevenueData.periodIndex00Amount) + periodAmount;
                          }
                          break;
                        case 2:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex02Amount = t.parseCurrency(projRenoRevenueData.periodIndex01Amount) + periodAmount;
                          }
                          break;
                        case 3:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex03Amount = t.parseCurrency(projRenoRevenueData.periodIndex02Amount) + periodAmount;
                          }
                          break;
                        case 4:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex04Amount = t.parseCurrency(projRenoRevenueData.periodIndex03Amount) + periodAmount;
                          }
                          break;
                        case 5:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex05Amount = t.parseCurrency(projRenoRevenueData.periodIndex04Amount) + periodAmount;
                          }
                          break;
                        case 6:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex06Amount = t.parseCurrency(projRenoRevenueData.periodIndex05Amount) + periodAmount;
                          }
                          break;
                        case 7:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex07Amount = t.parseCurrency(projRenoRevenueData.periodIndex06Amount) + periodAmount;
                          }
                          break;
                        case 8:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex08Amount = t.parseCurrency(projRenoRevenueData.periodIndex07Amount) + periodAmount;
                          }
                          break;
                        case 9:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex09Amount = t.parseCurrency(projRenoRevenueData.periodIndex08Amount) + periodAmount;
                          }
                          break;
                        case 10:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex10Amount = t.parseCurrency(projRenoRevenueData.periodIndex09Amount) + periodAmount;
                          }
                          break;
                        case 11:
                          if (calculateForPeriod) {
                            projRenoRevenueData.periodIndex11Amount = t.parseCurrency(projRenoRevenueData.periodIndex10Amount) + periodAmount;
                          }
                          break;
                        default:
                          break;
                      }
                    }       

                itemsToUpdateGrid.push(projRenoRevenueData);
                datasToSave.push(t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? t.generateUnitTypeWorksheetEntriesFromData(projRenoRevenueData) : t.generatePropWorksheetEntriesFromData(projRenoRevenueData));

                t.gridOptions.api.applyTransaction({ update: itemsToUpdateGrid });
                break;
              case t.PROJECTED_RENO_REVENUE:
              case t.UNIT_TYPE_PROJECTED_RENO_REVENUE:
                datasToSave.push(t.worksheetParameters.trackRenovationsAtUnitTypeLevel ? t.generateUnitTypeWorksheetEntriesFromData(params.data) : t.generatePropWorksheetEntriesFromData(params.data));
                break;
              default:
                break;
            }

            // settings determine which type of worksheet entries to save
            if (t.worksheetParameters.trackRenovationsAtUnitTypeLevel) {
              var updateUnitTypeData = await worksheetService.updateUnitTypeWorksheetEntries(t.budgetIterationId,
                datasToSave, t.budgetIterationLastModifiedTimestamp);
              // update timestamp
              if (updateUnitTypeData.lastModifiedTimestamp) {
                t.budgetIterationLastModifiedTimestamp = updateUnitTypeData.lastModifiedTimestamp;
              }
            } else {
              var updatePropertyData = await worksheetService.updatePropertyWorksheetEntries(t.budgetIterationId,
                datasToSave, t.budgetIterationLastModifiedTimestamp);
              // update timestamp
              if (updatePropertyData.lastModifiedTimestamp) {
                t.budgetIterationLastModifiedTimestamp = updatePropertyData.lastModifiedTimestamp;
              }
            }

            await t.communicateEntriesUpdated();

            t.changeLoading(false);
            break;
          default:
            break;
        }
      },


      getPeriodAmount(periodIndex, data, getPrevious) {
        switch (periodIndex) {
          case 1:
            return getPrevious ? data.periodIndex00Amount : data.periodIndex01Amount;
          case 2:
            return getPrevious ? data.periodIndex01Amount : data.periodIndex02Amount;
          case 3:
            return getPrevious ? data.periodIndex02Amount : data.periodIndex03Amount;
          case 4:
            return getPrevious ? data.periodIndex03Amount : data.periodIndex04Amount;
          case 5:
            return getPrevious ? data.periodIndex04Amount : data.periodIndex05Amount;
          case 6:
            return getPrevious ? data.periodIndex05Amount : data.periodIndex06Amount;
          case 7:
            return getPrevious ? data.periodIndex06Amount : data.periodIndex07Amount;
          case 8:
            return getPrevious ? data.periodIndex07Amount : data.periodIndex08Amount;
          case 9:
            return getPrevious ? data.periodIndex08Amount : data.periodIndex09Amount;
          case 10:
            return getPrevious ? data.periodIndex09Amount : data.periodIndex10Amount;
          case 11:
            return getPrevious ? data.periodIndex10Amount : data.periodIndex11Amount;
          case 0:
          default:
            return getPrevious ? data.priorYearLastMonth : data.periodIndex00Amount;
        }
      },    
      isCellEditable(params) {
        if (this.readonly) {
          return false;
        }
        var t = this;
        if (params.data.isEditable) {
          switch (params.colDef.colId) {
            case t.PERIOD_INDEX_00_AMOUNT:
            case t.PERIOD_INDEX_01_AMOUNT:
            case t.PERIOD_INDEX_02_AMOUNT:
            case t.PERIOD_INDEX_03_AMOUNT:
            case t.PERIOD_INDEX_04_AMOUNT:
            case t.PERIOD_INDEX_05_AMOUNT:
            case t.PERIOD_INDEX_06_AMOUNT:
            case t.PERIOD_INDEX_07_AMOUNT:
            case t.PERIOD_INDEX_08_AMOUNT:
            case t.PERIOD_INDEX_09_AMOUNT:
            case t.PERIOD_INDEX_10_AMOUNT:
            case t.PERIOD_INDEX_11_AMOUNT:
              switch (params.data.rowType) {
                case t.NUM_OF_UNIT_RENOS:
                  return !t.worksheetParameters.trackRenovationsAtUnitTypeLevel;
                case t.UNIT:
                  return false;
                default:
                  return true;
              }
            default:
              return false;
          }
        }
        return false;
      },
      rowIsHeader(rowType) {
        return rowType == this.UNIT_TYPE_HEADER || rowType == this.UNIT_HEADER;
      },
      selectGroupedCellRenderer(params) {
        let t = this;
        if (params.data.hasUnitRecords) {
          return {
            component: 'GroupedRowButton',
            params: {
              show: function () { return true; },
              allCollapsed: function (params) {
                return !params.data.showUnitRecords;
              },
              onClick: function (params) {
                let show = !params.data.showUnitRecords;
                params.api.forEachNode(node => {
                  if (node.data.tag == params.data.tag) {
                    node.setDataValue('showUnitRecords', show);
                  }
                });
                t.refreshDataFilter();
              },
            }
          }
        } else {
          return null;
        }
      },
      selectPeriodCellRenderer(params) {
        let t = this;
        if (params.data.rowType == t.UNIT && t.getPeriodAmount(t.getPeriodIndex(params.colDef.colId), params.data, false)) {
          return {
            component: 'LeaseExpiresReadonlyCell',
          }
        } else {
          return null;
        }
      },
      selectTotalCellRenderer(params) {
        let t = this;
        if (params.data.rowType == t.NUM_OF_UNIT_RENOS) {
          return {
            component: 'NumOutOfNumReadonlyCell',
            params: {
              dividend: function (params) {
                return params.value;
              },
              divisor: function () {
                return t.worksheetParameters.desiredRenovatedUnitsCount == null ? 0 : t.worksheetParameters.desiredRenovatedUnitsCount;
              },
            }
          }
        } else {
          return null;
        }
      },
      shouldRowsBeFiltered() {
        return true;
      },
      shouldShowRow(node) {
        return node.data.isUnitRecord == false ||
          node.data.showUnitRecords;
      },
      refreshDataFilter() {
        this.gridOptions.api.onFilterChanged();
      },
      entryGetter(params) {
        var t = this;
        var tempTotal = 0;
        var periodAmount;

        var periodIndex = t.getPeriodIndex(params.colDef.colId);

        switch (params.colDef.colId) {
          case t.PERIOD_INDEX_00_AMOUNT:
          case t.PERIOD_INDEX_01_AMOUNT:
          case t.PERIOD_INDEX_02_AMOUNT:
          case t.PERIOD_INDEX_03_AMOUNT:
          case t.PERIOD_INDEX_04_AMOUNT:
          case t.PERIOD_INDEX_05_AMOUNT:
          case t.PERIOD_INDEX_06_AMOUNT:
          case t.PERIOD_INDEX_07_AMOUNT:
          case t.PERIOD_INDEX_08_AMOUNT:
          case t.PERIOD_INDEX_09_AMOUNT:
          case t.PERIOD_INDEX_10_AMOUNT:
          case t.PERIOD_INDEX_11_AMOUNT:
            if (t.worksheetParameters.trackRenovationsAtUnitTypeLevel && params.data.rowType == t.NUM_OF_UNIT_RENOS) {
              t.gridOptions.api.forEachNode(node => {
                if (node.data.rowType == t.UNIT_TYPE_NUM_OF_UNIT_RENOS) {                 
                  periodAmount = t.getPeriodAmount(periodIndex, node.data, false);
                  tempTotal = tempTotal + (periodAmount == null ? 0 : periodAmount);
                  }                
              });
              return tempTotal;
            } else {
              return t.getPeriodAmount(periodIndex, params.data, false);
            }    
          case t.TOTAL_COL_ID:
            if (t.worksheetParameters.trackRenovationsAtUnitTypeLevel && params.data.rowType == t.NUM_OF_UNIT_RENOS) {              
              return params.getValue(t.PERIOD_INDEX_00_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_01_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_02_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_03_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_04_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_05_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_06_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_07_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_08_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_09_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_10_AMOUNT)
                + params.getValue(t.PERIOD_INDEX_11_AMOUNT);
            } else {
              return params.data.rowType == t.UNIT_TYPE_NUM_OF_UNIT_RENOS || params.data.rowType == t.NUM_OF_UNIT_RENOS ? t.calculateTotalAsInteger(params.data) : t.calculateTotalAsCurrency(params.data);              
            }            
          default:
            return params.value;
        }
      },     
      entryParser(params) {
        return params.data.rowType == this.NUM_OF_UNIT_RENOS || params.data.rowType == this.UNIT_TYPE_NUM_OF_UNIT_RENOS ? this.positiveIntegerParser(params) : this.currencyParser(params);
      },
      positiveIntegerParser(params) {
        var n = Number(params.newValue);
        if (isNaN(n)) { return null; }
        if (n < 0) { return 0; }
        var r = this.halfRoundUp(n, 0);
        return r;
      },
      currencyParser(params) {
        var n = this.parseCurrency(params.newValue, false);
        if (isNaN(n)) { n = null; }
        return n;
      },
      entryFormatter(params) {
        return params.value == null ? '' : params.data.rowType == this.NUM_OF_UNIT_RENOS || params.data.rowType == this.UNIT_TYPE_NUM_OF_UNIT_RENOS ? params.value : this.formatCurrency(params.value, true, true);
      },
      openComments() {
        this.$emit('openComments', 'renovations');
      },
      async refreshCommentIcon() {
        var commentsResults = await worksheetService.commentsExist(this.budgetIterationId, 2);
        this.tabHasUnresolvedComments = commentsResults.unresolvedFlagExists;
        this.tabHasComments = commentsResults.hasComments;
        console.log('refresh comment renovations');
      }
    },
    computed: {
      ...mapGetters(['currentLoading']),
      gridContainerStyle() {
        return 'height: ' + this.gridContainerHeight + 'px;';
      },
      currentData() {
        return this.renovationData;
      },
      currentHeaders() {
        return this.availableColumns;
      },
      commentIconColor() {
        return this.tabHasUnresolvedComments ? 'var(--v-resRed-darken1)' : 'var(--v-resBlue-darken1)';
      },
      commentIcon() {
        return this.tabHasUnresolvedComments ? 'mdi-flag' : this.tabHasComments ? 'mdi-comment-text-multiple' : 'mdi-comment-multiple-outline';
      },
      menuIcon() {
        return this.menuOpen ? 'mdi-dots-horizontal-circle' : 'mdi-dots-horizontal';
      },
      menuColor() {
        return 'var(--v-resBlue-darken1)';
      }
    },
    watch: {
      currentLoading: function () {
        if (this.currentLoading == true) {
          this.showGridLoadingOverlay(this.gridOptions);
        } else {
          this.hideGridLoadingOverlay(this.gridOptions);
        }
      },
      commentIteration: function () {
        this.refreshCommentIcon();
      }
    },
    async mounted() {
      await this.load();
      await this.refreshCommentIcon();
    },
    async created() {
      this.$eventHub.$on('budgetSettingsUpdated', this.load);
    },
  };
</script>

<style lang="scss" scoped>
  .renovations-container {
    .v-input--selection-controls::v-deep {
      margin: 0 !important;
      padding: 0 !important;
    }

    .v-text-field--outlined.v-input--is-focused::v-deep fieldset {
      border-color: var(--v-resBlue-darken1) !important;
    }

    .v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state)::v-deep fieldset {
      border-color: var(--v-resGrey-darken1) !important;
    }

    .grid-controls {
      height: 40px;
      margin-bottom: 14px;
      margin-top: 10px;
    }

    .function-controls {
      border: solid 1px var(--v-resGrey-lighten3);
      padding: 0px 6px 0px 6px;
      height: 100%;
      margin-right: 5px;
    }

    .natural-language-words {
      margin-left: 10px;
      margin-right: 10px;
    }

    .natural-language-inputs {
      height: 40px;
      width: 70px;
    }

    .help-controls {
      height: 100%;
      float: right;
      border: solid 1px var(--v-resGrey-lighten3);
      padding: 7px 6px 6px 6px;
      cursor: pointer !important;
    }

    .ellipses-control {
      height: 20px;
      width: 20px;
      margin-left: 10px;
      margin-bottom: 8px;
    }

    .ellipses-control-option-list {
      border-radius: 6px;
      border: 1px solid var(--v-resBlue-darken1);
    }

    .ellipses-control-option {
      font-size: 13px;
    }
  }
</style>
